import { PagesWithDropdown } from './Constants';

export type VehicleType = {
  vrn: string;
  vin?: string;
  make: string;
  model: string;
  engineSize: string;
  errorOrigin?: 'vrn' | 'vin' | 'email';
  errorMessage: string;
  modelGroup: string;
  modelTrimId: number;
  registrationDate: string;
  vehicleType: string;
  fuelType: string;
  fuelTypeId: number;
  fuelTypeCode: string;
  enginePowerBHP?: number;
  enginePowerKWH?: number;
  driveTypeId?: number;
  modelType: string;
  makeId: string;
  vehicleTypeId: string;
  modelId: string;
  considerRegistrationDate?: boolean;
};

export type JourneyState = {
  vehicle: VehicleState;
  products: ProductType[];
  documents: DocumentTreeItem[];
  personalDetail: PersonalDetailType;
  directDebitDetails: DirectDebitDetails;
  agreedTermsAndConditions: boolean;
  renewalMarketing: boolean;
  orderNumbers: string[];
  navigationStatus: StepperStatus;
  singlePaymentDetails: SinglePaymentDetails;
  registrationFailMsg: string;
  registrationState: number;
  expandedDocIds: string[];
  foundExistingQuotes: boolean;
  bankAuthorisationCode: string;
  dealerCode: string;
  token: string;
  policyStartDate: Date;
  isLateRenewal: boolean;
  promoCode: string;
  quoteId: number;
  hasQuoteExpired: boolean;
  quoteProducts: QuoteProduct[];
  isExpiredMessageAcknowledged: boolean;
  exceedsUpsellMileageLimit?: boolean;
  isOpensiteUser?: boolean;
  quotedByDealerReference?: string;
};

export type JourneyContext = {
  journey: JourneyState;
  onUpdateVehicleDetails: (vehicle: VehicleState) => void;
  onChangeSalesId: (salesId: string) => void;
  onUpdateProducts: (
    products: ProductType[],
    updateDocuments?: boolean
  ) => void;
  onUpdateQuotedProducts: (quoteProducts: QuoteProduct[]) => void;
  onUpdateProductDocuments: (documents: DocumentTreeItem[]) => void;
  onUpdateProductVariant: (productTypeId: number, variantId: string) => void;
  setPersonalDetails: (personalDetail: PersonalDetailType) => void;
  setAddressSinglePayment: (addressDetails: AddressDetails) => void;
  setDirectDebitDetails: (directDebitDetails: DirectDebitDetails) => void;
  onUpdateAgreedTermsAndConditions: (hasAgreed: boolean) => void;
  onUpdateRenewalMarketing: (consentFlag: boolean) => void;
  onRegister: (
    cardTransactionId: string,
    bankAuthorisationCode: string
  ) => void;
  onPaymentFailure: () => void;
  onPaymentVoided: () => void;
  onPageChange: (page: string, status: boolean) => void;
  getPolicy: (
    paymentMethod: string,
    cardTransactionId: string,
    noOfInstallments: number
  ) => PolicyServer | undefined;
  setRegistrationFailMsg: (message: string) => void;
  setQuoteId?: (id: number) => void;
  setHasQuoteExpired?: (hasQuoteExpired: boolean) => void;
  setExpandedDocIds: (docIds: string[]) => void;
  onUpdateFoundExistingQuotes: (foundExistingQuotes: boolean) => void;
  onPolicyStartDateChange: (policyStartDate: Date) => void;
  onChangeMileage: (currentMileage: number | null) => void;
  onChangeVehiclePurchasePrice: (e: React.FormEvent<HTMLInputElement>) => void;
  onChangeVehicleDeliveryDate: (vehicleDeliveryDate: string) => void;
  onUpdateVehicleVrn: (vrn: string) => void;
  onChangeVehicleGapIntroductionDate: (
    e: React.FormEvent<HTMLInputElement>
  ) => void;
  setIsExpiredMessageAcknowledged?: (
    isExpiredMessageAcknowledged: boolean
  ) => void;
  setExceedsUpsellMileageLimit?: (exceedsUpsellMileageLimit: boolean) => void;
  onUpdateQuotedByDealerReference?: (quotedByDealerReference: string) => void;
  onSetJourney: (journeyState: JourneyState) => void;
};

export type VehicleState = {
  vehicleDetails: VehicleType;
  currentMileage: number | null;
  vehiclePurchasePrice: number;
  vehicleDeliveryDate?: string;
  vehicleGapIntroductionDate?: string;
  salesId: string;
  dealerReference: string;
  deliveryDateEstimated: boolean;
};

export type ManualQuerySelections = {
  firstRegistrationDate?: string;
  firstRegistrationDateNew?: string;
  vehicleTypeId: string;
  vehicleMakeId: string;
  vehicleModelId: string;
  vehicleFuelTypeId: string;
  vehicleEngineSizeId: string;
  vehicleModelTrimId: string;
  vehicleType: string;
  make: string;
  model: string;
  fuelType: string;
  engineSize: string;
};

export type ProductVariant = {
  variantId: string;
  productId: number;
  duration: string;
  price?: number;
  noOfInstallments: number;
  paymentMethod: string;
  minimumPrice: number;
  maximumPrice: number;
  warrantyTierId: number;
  customerFriendlyName: string;
  tyreTypeId: number;
  claimLimit: number;
  isSelected: boolean;
  maxNoOfClaims: number;
  linkedProducts: LinkedProduct[];
  coverLevelId: number;
};

export type ProductDocument = {
  displayName: string;
  documentTypeId: number;
  code: string;
  warrantyTierId: number;
  productTypeId: number;
  productId: number;
};

export type LinkedProduct = {
  isMandatory: boolean;
  name: string;
  price: number;
  variantId: string;
  warrantyTierId: number;
  warrantyCoreId: number;
  typeId: number;
  paymentMethod: string;
  productDocuments: ProductDocument[];
};

export type DocumentTreeItem = {
  id: string;
  name: string;
  path?: string;
  level: number;
  show: boolean;
  showIcon?: boolean;
  children?: DocumentTreeItem[];
};

export type ProductData = {
  products: ProductType[];
  documents: DocumentTreeItem[];
  hasProducts: boolean;
};

export type SelectedLinkedProduct = {
  quoteCardId: number;
  linkedProductTypeId: number;
};

export type ProductType = {
  productTypeId: number;
  productId: number;
  productName: string;
  policyStartDateLimit: number;
  isSelected: boolean;
  policyStartDate?: string;
  readOnlyPolicyStartDate?: boolean;
  selectedTyreTypeId?: number;
  selectedClaimLimit?: number;
  selectedCoverLevelId?: number;
  selectedPaymentMethod?: string;
  selectedLinkedProducts?: SelectedLinkedProduct[];
  productVariants: ProductVariant[];
  isVariablePrice: boolean;
  productDocuments: ProductDocument[];
  effectiveDate: string;
  expiryDate: string;
  renewalGroupId: string;
  isPurchased: boolean;
  parentProductTypeId: number;
  productTypeCode: string;
  productSubTypeId: number;
};

export type ProductFilter = {
  dealerCode: string;
  firstRegistrationDate: string;
  vehiclePurchaseDate: string;
  vehicleValue: number;
  currentMileage: number;
  modelTrimId: number;
  fuelTypeId: string;
  engineSize: number;
  enginePowerBHP: number;
  enginePowerKWH: number;
  driveTypeId: string;
  vrn: string;
  channel: number;
  promoCode?: string;
  gapDate?: string;
  isOpenSite?: boolean;
  countryCode?: string;
};

export type AddressDetails = {
  addressList?: string;
  addressLine1?: string;
  addressLine2?: string;
  townOrCity?: string;
  region?: string;
  country?: string;
  postCode?: string;
};

export type PersonalDetailType = {
  title: string;
  firstName: string;
  surname: string;
  dateOfBirth?: Date;
  email: string;
  confirmEmail: string;
  phone: string;
  addressDetails: AddressDetails;
};

export enum QuoteStatus {
  Partial,
  Full
}

export type InsuranceJourneyState = {
  tiaProductCode: string;
  quoteId: number;
  policyNumber: string;
  source?: string;
  savingQuote: boolean;
  pageLoading: boolean;
  quoteStatus: QuoteStatus;
  personalDetails: InsuranceJourneyPersonalDetailsType;
  drivingHistoryDetails: InsuranceJourneyDrivingHistoryDetailsType;
  vehicleInfo: InsuranceJourneyVehicleDetailsType;
  coverDetails: InsuranceJourneyCoverDetailsType;
  addressDetails: InsuranceJourneyAddressDetailsType;
  insuranceProducts: InsuranceJourneyInsuranceProductsType;
  additionalDriverDetails: InsuranceJourneyAdditionalDriverDetailsType;
  confirmationDetails: InsuranceJourneyConfirmationDetailsType;
  aboutTermsOfBusinessAndFairUseDetails: InsuranceJourneyAboutTermsOfBusinessAndFairUseDetailsType;
  importantNoticeInformationDetails: InsuranceJourneyImportantNoticeInformationDetailsType;
  paymentDetails: InsuranceJourneyPaymentDetailsType;
  insuranceNavigationStatus: InsuranceStepperStatus;
  staticData: DropdownData[];
  dealerHomeOptions: DealerHomepageOptions;
  dealerRepresentative?: string;
  dealerUsername?: string;
};

export type DropdownData = {
  page: PagesWithDropdown;
  controlName: string;
  values: SelectOpt[];
};

export type InsuranceJourneyPersonalDetailsType = {
  title: string;
  gender: string;
  firstName: string;
  lastName: string;
  dateOfBirth?: Date;
  email: string;
  homePhone: string;
  mobilePhone: string;
  maritalStatus: string;
  residentSinceBirth: boolean;
  residentSinceDate?: Date;
  employmentStatus: string;
  occupation: string;
  occupationKey?: string;
  industry: string;
  industryKey?: string;
  ownHome?: boolean;
  expiryMonth: string;
};

export type InsuranceJourneyDrivingHistoryDetailsType = {
  licenceType: string;
  yearsLicenceHeld: string;
  hadClaimAccidentLosses?: boolean;
  claims: Claim[];
  hadMedicalConditionOrDisabilities?: boolean;
  medicalConditions: MedicalCondition[];
  hadDrivingConvictions?: boolean;
  drivingConvictions: DrivingConviction[];
  hasNonMotoringConvictions?: boolean;
  hadDeclinedInsurance?: boolean;
  noClaimsBonusYear: string;
  ncdDiscount?: boolean;
};

export type InsuranceJourneyVehicleDetailsType = {
  vrnKnown?: boolean;
  userVrn: string;
  manufacturer: string;
  model: string;
  fuelType: string;
  transmissionType: string;
  numberOfDoors: string;
  isItNewOrUsed: string;
  yearOfManufacture: string;
  keysPickupDate: Date | undefined;
  hasTrackingDevice?: boolean;
  trackingDevice: string;
  trackingDeviceKey?: string;
  hasBeenModified?: boolean;
  modifications: Modification[];
  vehicle?: VehicleType;
  vendor?: string;
  dealer: string;
  carValue?: string;
  vehicleSpecification: string;
};

export type Modification = {
  modificationType: string;
  forDisability: string;
};

export type InsuranceJourneyCoverDetailsType = {
  estimatedMileage: string;
  carUses: string;
  coverStartDate?: Date;
  coverStartTime: string;
  registeredVehicleKeeper: string;
  registeredVehicleOwner: string;
  paymentMode: string;
  estimatedBusinessMileage: string;
};

export type InsuranceJourneyAddressDetailsType = {
  overnightParkedAddress: AddressDetails;
  isThisCorrespondenceAddress?: boolean;
  correspondenceAddress: AddressDetails;
  locationVehicleParkedOvernight: string;
  numberOfCarsAtHousehold: string;
};

export type InsuranceOptionalProduct = {
  optionalProductId: string;
  optionalProductName: string;
  optionalProductLessDescription: string;
  optionalProductMoreDescription: string;
  optionalProductPolicyDocumentId: number;
  optionalProductIPIDStatementDocumentId: number;
  optionalProductPrice: number;
  isOptionalProductSelected: boolean;
  optionalSubProductList: InsuranceOptionalSubProduct[];
};

export type InsuranceOptionalSubProduct = {
  optionalSubProductId: string;
  optionalSubProductName: string;
  optionalSubProductPrice: number;
  isOptionalSubProductSelected: boolean;
};

export type InsuranceJourneyInsuranceProductsType = {
  quoteListItems: string[];
  annualPayment: number;
  arrangementFee: number;
  excessCompulsoryPayment: number;
  voluntaryExcess: string;
  excessTotalPayment: number;
  monthlyInstallment: number;
  monthlyDeposit: number;
  monthlyFirstInstallment: number;
  monthlyInstallmentNumber: number;
  monthlyAPR: number;
  monthlyTotal: number;
  monthlyFacilityFee: number;
  maxRoundingDifference: number;
  policyDocumentId: number;
  ipidStatementDocumentId: number;
  importantInformationNoticeDocumentId: number;
  showNCDDocument: boolean;
  showNCDPrice: boolean;
  ncdPrice: number;
  optionalProductList: InsuranceOptionalProduct[];
  invalid: boolean;
};

export type InsuranceJourneyAdditionalDriverDetailsType = {
  whoWillDrive: string;
  title: string;
  gender: string;
  firstName: string;
  lastName: string;
  maritalStatus: string;
  relationWithPolicyHolder: string;
  dateOfBirth?: Date;
  residentSinceBirth: boolean;
  residentSinceDate?: Date;
  licenceType: string;
  yearsLicenceHeld: string;
  occupation: string;
  occupationKey?: string;
  industry: string;
  industryKey?: string;
  hadClaimAccidentLosses?: boolean;
  claims: Claim[];
  hadDrivingConvictions?: boolean;
  drivingConvictions: DrivingConviction[];
  nonMotoringConvictions?: boolean;
  hadMedicalConditionOrDisabilities?: boolean;
  medicalConditions: MedicalCondition[];
  declinedInsurance?: boolean;
};

export type InsuranceJourneyConfirmationDetailsType = {
  iAgree: boolean;
  keepingYouInformed: string;
  nextYearInsurance: string;
  yourDocuments: string;
  marketingViaPost: boolean;
  marketingViaPhone: boolean;
  marketingViaEmail: boolean;
  marketingViaTextMessage: boolean;
};

export type InsuranceJourneyAboutTermsOfBusinessAndFairUseDetailsType = {
  readAndAgreeWithTerms?: boolean;
  consentForDetailsToBePassed?: boolean;
};

export type InsuranceJourneyImportantNoticeInformationDetailsType = {
  readAndUnderstoodImportantInformationNotice?: boolean;
};

export type InsuranceJourneyPaymentDetailsType = {
  directDebitDetails?: DirectDebitDetails;
  cardPaymentDetails?: CardPaymentDetails;
  selectedPaymentMethod: PaymentMethod;
};

export type CommonEventDetailType = {
  flag: boolean;
};

export type InsuranceJourneyContext = {
  journey: InsuranceJourneyState;
  pageEvents: {
    personalDetailsPage: {
      onChangeTitle: (value: string) => void;
      onChangeGenderSelection: (value: string) => void;
      onChangeFirstName: (e: React.FormEvent<HTMLInputElement>) => void;
      onChangeLastName: (e: React.FormEvent<HTMLInputElement>) => void;
      onChangeDateOfBirth: (value: Date | undefined) => void;
      onChangeEmailAddress: (e: React.FormEvent<HTMLInputElement>) => void;
      onChangeHomePhone: (e: React.FormEvent<HTMLInputElement>) => void;
      onChangeMobilePhone: (e: React.FormEvent<HTMLInputElement>) => void;
      onChangeMaritalStatus: (value: string) => void;
      onChangeResidentSinceBirth: (value: boolean) => void;
      onChangeResidentSinceDate: (value: Date | undefined) => void;
      onChangeEmploymentStatus: (value: string) => void;
      onChangeOccupation: (value: string, key?: string) => void;
      onChangeIndustry: (value: string, key?: string) => void;
      onChangeOwnHome: (value: boolean) => void;
      onChangeExpiryMonth: (value: string) => void;
    };
    additionalDriverDetailsPage: {
      onSelectChange: (value: string, key: string) => void;
      onChangeGenderSelection: (value: string) => void;
      onChangeFirstName: (e: React.FormEvent<HTMLInputElement>) => void;
      onChangeLastName: (e: React.FormEvent<HTMLInputElement>) => void;
      onChangeDateOfBirth: (value: Date | undefined) => void;
      onChangeResidentSinceBirth: (value: boolean) => void;
      onChangeResidentSinceDate: (value: Date | undefined) => void;
      onChangeOccupation: (value: string, key?: string) => void;
      onChangeIndustry: (value: string, key?: string) => void;
      onUpdateClaims: (claims: Claim[]) => void;
      onUpdateDrivingConvictions: (convictions: DrivingConviction[]) => void;
      onUpdateMedicalConditions: (
        medicalConditions: MedicalCondition[]
      ) => void;
      onChangeDeclinedInsurance: (value: boolean) => void;
      onChangeHadMedicalConditionOrDisabilities: (value: boolean) => void;
      onChangeYearsLicenceHeld: (value: string) => void;
      onChangeHadClaimAccidentLosses: (value: boolean) => void;
      onChangeHadDrivingConvictions: (value: boolean) => void;
      onChangeNonMotoringConvictions: (value: boolean) => void;
    };
    drivingHistoryDetailsPage: {
      onChangeLicenceType: (value: string) => void;
      onChangeYearsLicenceHeld: (value: string) => void;
      onChangeHadClaimAccidentLosses: (value: boolean) => void;
      onChangeHadMedicalConditionOrDisabilities: (value: boolean) => void;
      onChangeHadDrivingConvictions: (value: boolean) => void;
      onChangeNonMotoringConvictions: (value: boolean) => void;
      onChangeDeclinedInsurance: (value: boolean) => void;
      onChangeNoClaimBonusYears: (value: string) => void;
      onUpdateClaims: (claims: Claim[]) => void;
      onUpdateDrivingConvictions: (convictions: DrivingConviction[]) => void;
      onUpdateMedicalConditions: (
        medicalConditions: MedicalCondition[]
      ) => void;
      onChangeNcdDiscount: (value: boolean) => void;
    };
    vehicleDetailsPage: {
      onChangeVrnKnown: (value: boolean, brand: string) => void;
      onChangeUserVrn: (value: string) => void;
      onChangeManufacturer: (value: string) => void;
      onChangeModel: (value: string) => void;
      onChangeFuelType: (value: string) => void;
      onChangeTransmissionType: (value: string) => void;
      onChangeNumberOfDoors: (value: string) => void;
      onChangeIsItNewOrUsed: (value: string) => void;
      onChangeYearOfManufacture: (value: string) => void;
      onChangeKeysPickupDate: (value: Date | undefined) => void;
      onChangeVehicle: (value: VehicleType | undefined) => void;
      onChangeHasTrackingDevice: (value: boolean) => void;
      onChangeTrackingDevice: (value: string, key?: string) => void;
      onChangeVehicleHasBeenModified: (value: boolean) => void;
      onChangeVendor: (value: string) => void;
      onChangeDealer: (value: string) => void;
      onChangeCarvalue: (value: string) => void;
      onUpdateModificationList: (value: Modification[]) => void;
      onChangeVehicleSpecification: (value: string) => void;
    };
    coverDetailsPage: {
      onChangeEstimatedMileage: (value: string) => void;
      onChangeCarUse: (value: string) => void;
      onChangeEstimatedBusinessMileage: (value: string) => void;
      onChangeCoverStartDate: (value: Date | undefined) => void;
      onChangeCoverStartTime: (value: string) => void;
      onChangeRegisteredVehicleKeeper: (value: string) => void;
      onChangeRegisteredVehicleOwner: (value: string) => void;
      onChangePaymentMode: (value: string) => void;
    };
    addressDetailsPage: {
      onChangeOvernightParkedAddress: (value: AddressDetails) => void;
      onChangeIsThisCorrespondenceAddress: (value: boolean) => void;
      onChangeCorrespondenceAddress: (value: AddressDetails) => void;
      onChangeLocationVehicleParkedOvernight: (value: string) => void;
      onChangeNumberOfCarsAtHousehold: (value: string) => void;
    };
    confirmationDetailsPage: {
      onChangeIAgree: (value: boolean) => void;
      onChangeKeepingYouInformed: (value: string) => void;
      onChangeNextYearInsurance: (value: string) => void;
      onChangeYourDocuments: (value: string) => void;
      onChangeMarketingViaPost: (value: boolean) => void;
      onChangeMarketingViaEmail: (value: boolean) => void;
      onChangeMarketingViaPhone: (value: boolean) => void;
      onChangeMarketingViaTextMessage: (value: boolean) => void;
    };
    aboutTermsOfBusinessAndFairUseDetailsPage: {
      onChangeReadAndAgreeWithTerms: (value: boolean) => void;
      onChangeConsentForDetailsToBePassed: (value: boolean) => void;
    };
    importantNoticeInformationDetailsPage: {
      onChangeReadAndUnderstoodImportantInformationNotice: (
        value: boolean
      ) => void;
    };
    paymentPage: {
      onChangeDirectDebitDetails: (value: DirectDebitDetails) => void;
      setCardPaymentDetails: (transactionId: string) => void;
    };
    yourQuotePage: {
      onVoluntaryExcessChange: (value: string) => void;
      onShowNCDPrice: (value: boolean) => void;
    };
    optionalCoverPage: {
      onUpdateSelectedOptionalProduct: (
        optionalProductList: InsuranceOptionalProduct[]
      ) => void;
    };
    dealerLoginPage: {
      setDealerHomepageOptions: (
        dealerHomepageOptions: DealerHomepageOptions
      ) => void;
      setDealerUsername: (dealerUsername: string) => void;
      setDealerRepresentative: (dealerRepresentative: string) => void;
    };
    commonEvents: {
      setCustomerSelfRegistration: (
        vendor: string,
        dealer: string,
        dealerHomeOptions: DealerHomepageOptions
      ) => void;
      setSavingQuote: (flag: boolean) => void;
      setPageLoading: (flag: boolean) => void;
      onPageChange: (page: string, status: boolean) => void;
      setQuote: (quote: InsuranceJourneyState) => void;
      setSelectedPaymentMethod: (paymentMethod: PaymentMethod) => void;
      setStaticData: (dropdownData: DropdownData[]) => void;
    };
  };
};

export type SelectOpt = {
  id: string;
  text: string;
  value: string;
  disabled: boolean;
};

export type DatalistOption = {
  text: string;
  value: string;
};

export type Country = {
  iso3code: string;
  countryName: string;
  alpha2code: string;
};

export type AddressOption = {
  text: string;
  value: string;
};

export type AddressInfo = {
  addressLine1: string;
  addressLine2: string;
  townOrCity: string;
  region: string;
  countryIso3Code: string;
  postcode: string;
};

export type DirectDebitDetails = {
  accountHolderName: string;
  sortCode1: string | null;
  sortCode2: string | null;
  sortCode3: string | null;
  accountNumber: string | null;
  selectedPaymentDay: string;
  bankName: string | null;
};

export type CardPaymentDetails = {
  transactionId: string;
};

export enum PaymentMethod {
  annually,
  monthly
}

export type ProductVariantServer = {
  variantId: string;
  duration: string;
  price: number;
  noOfInstallments: number;
  paymentMethod: string;
  warrantyTierId: number;
};

export type ProductServer = {
  productTypeId: number;
  productName: string;
  policyStartDateLimit: number;
  productVariants: ProductVariantServer[];
  policyStartDate: string | undefined;
  isVariablePrice: boolean;
};

export type RegisterPolicyDto = {
  allowPastPurchaseDate: boolean;
  policy: PolicyServer[];
};

export type PolicyServer = {
  dealerNumber: string;
  vrn: string;
  vin: string;
  purchaseDistance: number;
  vehiclePurchasePrice: number;
  dateFirstRegistration: string;
  vehiclePurchaseDate: string;
  title: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneHome: string;
  phoneMobile: string;
  phoneWork: string;
  addressLine1: string;
  addressLine2: string;
  cityTown: string;
  postalCode: string;
  gapIntroductionDate: string;
  paymentMethod: string;
  bankSortCode: string;
  accHolderName: string;
  bankAccountNumber: string;
  bankName: string;
  paymentReference: string;
  products: ProductServer[];
  brand: string;
  journeyId: string;
  preferredPaymentDay: number;
  salesId: string;
  dateOfBirth: string;
  modelTrimId: number;
  isLateRenewal: boolean;
  useWorldPay: boolean;
  renewalConsent: boolean;
};

export type CardPayment = {
  amount: number;
  description: string;
  surname: string;
  firstName: string;
  address1: string;
  address2: string;
  city: string;
  postCode: string;
  country: string;
  state: string;
  phone: string;
  email: string;
};

export type PaymentAssistRequest = {
  paymentInfo: PaymentAssistCustomer;
  site: Site;
  journey: JourneyState;
  journeyType: number;
};

export type PaymentAssistCustomer = {
  amount: number;
  productVariant: string;
  surname: string;
  firstName: string;
  address1: string;
  address2: string;
  city: string;
  postCode: string;
  county: string;
  phone: string;
  email: string;
  vrn: string;
};

export type PaymentAssistBeginResponse = {
  token: string;
  url: string;
};

export type PaymentAssistStatus = {
  token: string;
  status: string;
  amount: number;
  expires_at: string;
  pa_ref: string;
  requires_invoice: boolean;
  has_invoice: boolean;
};

export type Totals = {
  noOfInstallments: number;
  selectedProductsCount: number;
  selectedFirstMonthlyPrice: number;
  selectedSubsequentMonthlyPrice: number;
  selectedSubscriptionPrice: number;
  selectedSinglePrice: number;
  selectedTotalPrice: number;
};

export type Result = {
  isSuccess: boolean;
  text: string;
};

export type VehicleInfoLookup = {
  id: string;
  text: string;
  value: string;
};

export type VehicleModelTrimLookup = {
  id: string;
  text: string;
  value: string;
  enginePowerBHP: number;
  enginePowerKWH: number;
  driveTypeId: number;
  vin: string;
  fuelTypeCode: string;
};

export type StepperStatus = {
  vehicle: boolean;
  products: boolean;
  personalDetails: boolean;
  checkout: boolean;
  confirmation: boolean;
  quote: boolean;
};

export type User = {
  userId: string;
  instanceId: string;
  dealerCode: string;
  token: string;
  isValid: boolean;
  error: string;
  recaptchaSiteKey: string;
  captchaDisable?: boolean;
  isDealer?: boolean;
  insuranceDealerName?: string;
  insuranceDealershipName?: string;
  insuranceDealerLoginUserName?: string;
  insuranceDealershipLocation?: string;
  insuranceDealerRepresentative?: string;
  insuranceDealerId?: number;
  insuranceDealerCode?: string;
};

export type Brand = {
  brandName: string;
  brandStyles: string;
  googleTagManagerId: string;
  worldPay: boolean;
  openSiteDealerCode: string;
  brandDisplayName: string;
  brandDisplayFullName: string;
  brandPhoneNumbers: BrandPhoneNumbers;
  useChangesNumberOnPolicyDocsPage: boolean;
  brandVanityUrl: string;
  brandAddress: string;
  tiaProductCode: string;
  showInsuranceVendorDropdown: boolean;
  showAssistanceLink: boolean;
  useAlternateEligibility: boolean;
  homeLink?: string;
  showMyAccount?: boolean;
  insuranceDocumentContent?: InsuranceDocumentContent;
  showArrangementFeeMessage?: boolean;
  showDealerQuestion?: boolean;
};

export type BrandPhoneNumbers = {
  quotesAndRenewalsNumber: string;
  changesAndComplaintsNumber: string;
  claimsNumber: string;
  excessReturnVoucherNumber: string;
  glassHelplineNumber: string;
  lostKeysNumber: string;
  breakdownAssistanceNumber: string;
  europeanBreakdownAssistanceNumber: string;
};

export type InsuranceDocumentContent = {
  ageasPrivateCar: string;
  allianzABC: string;
  allianzHighway: string;
  aXA: string;
  covea: string;
  excessReturnPolicy: string;
};

export type Site = {
  user: User;
  brand: Brand;
  content: Content;
  sagePayBaseUrl: string;
  journeyType: number;
  recordId?: string;
  renewalSourceSystem?: string;
  includeQuotes: boolean;
  includeOpenSite: boolean;
  isAppUnderMaintenance: boolean;
  maintenanceMessage: string;
  countryCode: string;
  cultureCode: string;
  quoteUpsell: boolean;
  tiaData: string;
  isInsuranceMobilise: boolean;
  vPol: string;
};

export type TitledListItem = {
  text: string;
  icon: string;
  file?: string;
  content?: string;
  externalLink?: string;
};

export type TitledList = {
  title: string;
  items: TitledListItem[];
};

export type DocumentItem = {
  documentName: string;
  documentPath: string;
};

export type Content = CmsContent;

export type CmsContent = {
  productPage: ProductPageCustomisation;
  landingPage: LandingPageCustomisation;
  directDebitPage: DirectDebitPageCustomisation;
  confirmationPage: ConfirmationPageCustomisation;
  common: CommonCustomisation;
  footer: FooterCustomisation;
  vehiclePage: VehiclePageCustomisation;
  customerPage: CustomerPageCustomisation;
  singlePaymentPage: SinglePaymentPageCustomisation;
  productVariantPage: ProductVariantPageCustomisation;
  summaryPage: SummaryPageCustomisation;
  reasonsToProtect: TitledList;
  quotePage: RenewalQuotePageCustomisation;
  reasonsToExtend: TitledList;
  usefulDocuments: DocumentItem[];
  allureScript: string;
};

interface PageCustomisation {
  title: string;
  description: string;
  information: string;
  warning: string;
}

export interface LandingPageCustomisation extends PageCustomisation {
  backgroundImageUrl: string;
  videoUrl: string;
  title: string;
  description: string;
  information: string;
  warning: string;
  journeyTimeNote: string;
  anyQuestionsSection: AnyQuestionsSectionCustomisation;
  whySection: WhySectionCustomisation;
  videoAlternativeImageUrl: string;
  wistiaEmbedUrl: string;
  useVinInput: boolean;
  forceVinCheck: boolean;
  vinNotFoundText: string;
}

export interface VehiclePageCustomisation extends PageCustomisation {
  justSoYouKnow: string;
  upsellAnnualMileage: string;
  hideDealerReference: boolean;
  hidePurchasePrice: boolean;
  hideDeliveryDate: boolean;
  hideMileage: boolean;
  editablePurchasePrice: boolean;
  editableMileage: boolean;
  hideEstimatedDeliveryDate: boolean;
  mandatorySalesId: boolean;
}

export interface CustomerPageCustomisation extends PageCustomisation {
  justSoYouKnow: string;
  isDateOfBirthOptional: boolean;
  phoneNumberLabel: string;
}

export interface ProductPageCustomisation extends PageCustomisation {
  productTypeCustomisation: ProductTypeCustomisation[];
  justSoYouKnow: string;
  noProductsFound: string;
}

export interface ProductVariantPageCustomisation extends PageCustomisation {
  justSoYouKnow: string;
  disclaimer: string;
  paymentProfileLabel: string;
}

export interface SummaryPageCustomisation extends PageCustomisation {
  justSoYouKnow: string;
  acceptanceText: string[];
  acceptanceTickboxText: string;
  consentToRenewalMarketingTickboxText: string;
}

export interface DirectDebitPageCustomisation extends PageCustomisation {
  justSoYouKnow: string;
  guaranteePayee: string;
}

export interface SinglePaymentPageCustomisation extends PageCustomisation {
  subscriptionInformation: string;
  justSoYouKnow: string;
}

export interface ConfirmationPageCustomisation extends PageCustomisation {
  summaryDescription: string;
  emailConfirmation: string;
  backgroundImageUrl: string;
}

export type CommonCustomisation = {
  navLogoUrl: string;
  navMobileLogoUrl: string;
  faviconUrl: string;
  hideCustomerPlan: boolean;
};

export type FooterCustomisation = {
  contactDetailItems: TitledList;
  legalDocumentsItems: TitledList;
  tradingNameItems: TitledList;
};

export type ProductTypeCustomisation = {
  productType: number;
  name: string;
  seeMoreItems: string[];
  synopsisText: string;
  icon: string;
  videoUrl: string;
  imageUrl: string;
  gapCoolOffWarning: string;
  showKeyBenefitsInProductCard?: boolean;
};

export type KeyBenefitList = {
  listId: number;
  title: string;
  keyBenefits: string[];
};

export type CoverLevelCustomisation = {
  title: string;
  warrantyCoreId: string;
  keyBenefitLists: KeyBenefitList[];
};

export type RenewalProductTypeCustomisation = {
  productTypeId: number;
  coverLevels: CoverLevelCustomisation[];
};

export type WhySectionCustomisation = {
  whySectionHeader: string;
  whySectionDescription: string;
  whySectionLists: WhySectionListCustomisation[];
};

export type WhySectionListCustomisation = {
  whySectionListHeader: string;
  caveatText: string;
  whySectionListItems: WhySectionListItemCustomisation[];
};

export type WhySectionListItemCustomisation = {
  whySectionItemTitle: string;
  whySectionItemDescription: string;
  whySectionItemLogoUrl: string;
};

export type AnyQuestionsSectionCustomisation = {
  landingPageQuestionsSectionHeader: string;
  landingPageQuestionsSectionDescription: string;
  landingPageQuestionsSectionNumber: string;
  landingPageQuestionsSectionEmail: string;
  openingHours: OpeningHoursCustomisation[];
};

export type OpeningHoursCustomisation = {
  openingDays: string;
  openingTimes: string;
  availableServices: string;
};

export interface RenewalQuotePageCustomisation extends PageCustomisation {
  justSoYouKnow: string;
  selectionText: string;
  productTypeCustomisations: RenewalProductTypeCustomisation[];
}

export type PolicyResponse = {
  isValid: boolean;
  errorMessage: string;
  policyNumber: string;
};

export type PaymentSuccessResponse = {
  success: boolean;
  do3dAuth: boolean;
  transactionId: string;
  acsUrl: string;
  cReq: string;
  errorMessage: string;
  statusCode: string;
  bankAuthorisationCode: string;
};

export type CardRequest = {
  cardIdentifier: string;
  merchantSessionKey: string;
  cardPayment: CardPayment;
  browserLanguage: string;
  browserJavaEnabled: boolean;
  browserColorDepth: string;
  browserScreenHeight: string;
  browserScreenWidth: string;
  browserTZ: string;
};

export type CardPaymentRequest = {
  cardRequest: CardRequest;
  countryCode: string;
};

export type SinglePaymentDetails = {
  billingAddress: AddressDetails;
};

export type DocumentDetails = {
  documentCode: string;
  documentName: string;
  documentData: string;
  contentType: string;
};

export type RenewalCredentials = {
  vrn: string;
  vin: string;
  password: string;
};

export type QuoteDetail = {
  name: string;
  priceSingle: number;
  priceMonthly: number;
  numberOfInstallments: number;
  type: string;
  variantId: string;
  isSelected: boolean;
  effectiveDate: string;
  duration: number;
};

export type RenewalDetails = {
  customerDetail: PersonalDetailType;
  vehicleDetail: VehicleType;
  dealerCode: string;
  token: string;
  expiryDate: Date;
  policyStartDate: Date | undefined;
  vehicleDeliveryDate: string;
  isLateRenewal: boolean;
  isRenewalPastExpiry: boolean;
  isRenewalExpiryApproved: boolean;
  promoCode: string;
};

export type CoverLevel = {
  value: number;
  text: string;
};

export type NavigationStep = {
  page: string;
  pageIndex: number;
  pageName: string;
};

export type QuotesData = {
  id: number;
  dealerNumber: number;
  vrn: string;
  vin: string;
  vehicleInfo: VehicleState;
  quotedProducts: QuoteProduct[];
  personalDetail: PersonalDetailType;
  products: ProductData;
  hasExpired: boolean;
  rollingQuote: boolean;
};

export type CustomerCredentials = {
  email: string;
  password: string;
  countryCode: string;
};

export type QuotesDto = {
  quoteHeader: QuoteHeader;
  productFilter: QuoteProductFilter;
  quoteProducts: QuoteProduct[];
};

export type QuoteRequest = {
  quote: QuotesDto;
  countryCode: string;
};

export type QuoteHeader = {
  id: number;
  password: string;
  customerEmail: string;
  customerTitle: string;
  customerSurname: string;
  vrn: string;
  vin: string;
  vehicleMake: string;
  vehicleModel: string;
  doNotContact: boolean;
  createdOn: Date;
  createdBy: string;
  deliveryDateEstimated: boolean;
  quoteType: number;
  dealerReference: string;
};

export type QuoteProductFilter = {
  versionNumber: number;
  dealerNumber: string;
  purchaseDate?: string;
  policyPurchaseDate?: Date;
  dateFirstRegistration?: string;
  modelTrimId?: number;
  fuelType: string;
  engineSize?: number;
  enginePowerBHP?: number;
  enginePowerKWH?: number;
  driveType: string;
  distance?: number;
  vehicleValue?: number;
  channels: string;
  promoCode: string;
  gapIntroductionDate?: string;
};

export type QuoteProduct = {
  productTypeId: number;
  versionNumber: number;
  variantId: string;
  variantName: string;
  productTypeName: string;
  quotedPrice: number;
  createdOn?: string;
  createdBy: string;
  expiryDate?: string;
  productSubTypeId?: number;
};

export type QuoteApiResponse = {
  isSaved: boolean;
  message?: string;
  quoteDto?: QuotesDto;
};

export type QuotePrice = {
  variantId: string;
  monthlyPrice: number;
  totalPrice: number;
  noOfInstallments: number;
  paymentMethod: string;
};

export type ProductTypeAndDocs = {
  productTypeId: number;
  productDocuments: ProductDocument[];
  selectedProductIds: number[];
};

export type BoughtProductsDto = {
  quoteId: number;
  dealerNumber: string;
  productTypeId: number[];
  bought: boolean;
};

export type Link = {
  displayText: string;
  url: string;
  className: string;
};

export type KeyValuePair = {
  key: string;
  value: string;
};

export type DrivingHistoryApiResponseModel = {
  licenceHeldYears: KeyValuePair[];
  claimTypes: KeyValuePair[];
  drivingBanLengths: KeyValuePair[];
  drivingConvictionTypes: KeyValuePair[];
  licenceTypes: KeyValuePair[];
  medicalConditions: KeyValuePair[];
  noClaimsBonusYears: KeyValuePair[];
};

export type AboutYouModel = {
  carUses: KeyValuePair[];
  drivers: KeyValuePair[];
  employmentStatuses: KeyValuePair[];
  estimatedMileages: KeyValuePair[];
  industryTypes: KeyValuePair[];
  maritalStatuses: KeyValuePair[];
  occupationTypes: KeyValuePair[];
  registeredKeepers: KeyValuePair[];
  registeredOwners: KeyValuePair[];
  titles: KeyValuePair[];
  genders: KeyValuePair[];
};

export type CarInsuranceVehicleModel = {
  dealers: KeyValuePair[];
  fuelTypes: KeyValuePair[];
  modificationTypes: KeyValuePair[];
  numberOfDoors: KeyValuePair[];
  vendors: KeyValuePair[];
  yearOfManufacture: KeyValuePair[];
  trackingDevices: KeyValuePair[];
  newOrUsed: KeyValuePair[];
};

export type DealerHomepageOptions = {
  brandOptions: string[];
  nonBrandOptions: string[];
  showDealerRegistration: boolean;
  showCustomerSelfRegistration: boolean;
  includeDriveaway: boolean;
  includeExcessReturn: boolean;
};

export type EmailResponse = {
  errorMessages: string[];
  success: boolean;
  payload: boolean;
};

export type CallMeBackEmailContent = {
  firstName: string;
  lastName: string;
  title: string;
  callDate: string | undefined;
  email: string;
  callTime: string;
  phoneNumber: string;
  brandName: string;
};

export type VSCRegisterEmailContent = {
  firstName: string;
  lastName: string;
  title: string;
  email: string;
  brandName: string;
};

export type NCDEmailContent = {
  firstName: string;
  lastName: string;
  email: string;
  postCode: string;
  brandName: string;
  attachment: ApiFile;
};

export type TreeItemData = {
  id: string;
  name: React.ReactNode;
  children: TreeItemData[];
};

export type Claim = {
  claimType: string;
  claimDate: Date | undefined;
};

export type DrivingConviction = {
  convictionType: string;
  convictionDate: Date | undefined;
  lengthOfBan: string;
  points: number | undefined;
};

export type CoverDetailsDropDowns = {
  carUses: KeyValuePair[];
  estimatedMileages: KeyValuePair[];
  vehicleOwners: KeyValuePair[];
};

export type MedicalCondition = {
  typeOfConditionKey?: string;
  typeOfCondition: string;
  hasDVLANotified: string;
  canKeepLicence: string;
};

export type AdditionalDriverDropDowns = {
  additionalDriverRelationships: KeyValuePair[];
  claimTypes: KeyValuePair[];
  drivers: KeyValuePair[];
  drivingBanLengths: KeyValuePair[];
  drivingConvictionTypes: KeyValuePair[];
  licenceTypes: KeyValuePair[];
  medicalConditions: KeyValuePair[];
  noClaimsBonusYears: KeyValuePair[];
  titles: KeyValuePair[];
};

export type AddressDetailsDropDowns = {
  overnightParkingLocations: KeyValuePair[];
  numberOfCars: KeyValuePair[];
};

export type DealerLoginResponseModel = {
  dealerCredentialResponse: DealerCredentialResponse;
  dealerHomeOptions: DealerHomepageOptions;
};

export type DealerCredentialResponse = {
  isDealer: boolean;
  token: string;
  name: string;
  dealershipName: string;
  dealerId: number;
  dealerCode: string;
  insuranceDealershipLocation?: string;
};

export type InsuranceStepperStatus = {
  aboutyou: boolean;
  drivingHistory: boolean;
  vehicleDetails: boolean;
  addressDetails: boolean;
  coverDetails: boolean;
  additonalDriverDetails: boolean;
  yourQuote: boolean;
  confirmation: boolean;
};

export type InsuranceJourneyMinMaxDobType = {
  maxDob: Date;
  minDob: Date;
};

export type ClaimError = {
  claimDate?: string;
  claimType?: string;
};

export type DrivingConvictionError = {
  convictionTypes?: string;
  lengthOfBan?: string;
  convictionDate?: string;
  points?: string;
};

export type MedicalConditionError = {
  typeOfCondition?: string;
  hasDVLANotified?: string;
  canKeepLicence?: string;
};

export type VehicleModificationError = {
  modificationType?: string;
  forDisability?: string;
};

export type DealerDashboardItem = {
  quote: string;
  customer: string;
  dealer: string;
  vehicleRegistrationNumber: string;
  make: string;
  newOrUsed: string;
  startDate: string;
  productType: string;
  status: string;
  postcode: string;
  includeDriveaway: boolean;
  includeExcessReturn: boolean;
  email: string;
  cnStatusCode: string;
};

export type ApiFile = {
  filename: string;
  filepath: string;
  filetype: string;
};

export type ActivateDealerQuoteDto = {
  quoteId: string;
  startDate: string;
  startTime: string;
  vrn: string;
  dealerRepresentative: string;
  excessProtectActive: boolean;
  coverNoteActive: boolean;
};

export type ActivateResponse = {
  activateStatus: string;
};

export type VinWarrantyExpiry = {
  id: number;
  vin: string;
  customerHandoverDate: Date;
  purchaseYear: number;
  oemWarranty: string;
  oemWarrantyExpiryDate: Date;
  expiryYear: number;
  expiryMonth: number;
  level2Country: string;
  brand: string;
  modelDescription: string;
};

export type translationLangaugeResource = {
  Language: string;
  Resources: Record<string, string>;
};
